import React, { Fragment, ReactNode } from 'react';

import {
  ParsedDateRenderer,
  ParsedTimeRenderer,
  RecordColumnMapType,
  RecordType,
} from '../shared';
import { infoRenderers } from './infoRenderers';
import { hasValue } from './RecordOverview';
import { RecordValue } from './RecordValue';

export const getRenderer =
  (template: string, emptyText: ReactNode = 'None') =>
  (_value: ReactNode, record: RecordType) =>
    template.split(/{(.*?)}/g).map((parseItem, index) => (
      <Fragment key={`record-prop-${index}`}>
        {record.hasOwnProperty(parseItem) ? (
          <RecordValue hideDots={true} name={parseItem} record={record}>
            {hasValue(record[parseItem]) ? record[parseItem] : emptyText}
          </RecordValue>
        ) : (
          parseItem
        )}
      </Fragment>
    ));

export const recordHeaders: RecordColumnMapType = {
  Audit: [
    { primary: 'metadata_vendor', secondary: 'metadata_product' },
    { renderer: infoRenderers.Audit },
  ],
  AuditChange: [
    { primary: 'metadata_vendor', secondary: 'metadata_product' },
    { renderer: infoRenderers.AuditChange },
  ],
  AuditResourceAccess: [
    { primary: 'metadata_vendor', secondary: 'metadata_product' },
    { renderer: infoRenderers.AuditResourceAccess },
  ],
  Authentication: [
    { primary: 'metadata_vendor', secondary: 'metadata_product' },
    { primary: 'srcDevice_ip', secondary: 'srcDevice_hostname' },
    {
      renderer: infoRenderers.Authentication,
      secondary: 'action',
    },
  ],
  AuthenticationPrivilegeEscalation: [
    { primary: 'srcDevice_ip' },
    { primary: 'action' },
    { renderer: infoRenderers.AuthenticationPrivilegeEscalation },
  ],
  Canary: [
    {
      primary: 'hatchtime',
      renderer: (value) =>
        typeof value === 'number' ? ParsedDateRenderer(value) : value,
      secondaryRenderer: (_value, { hatchtime }) =>
        // Do not render the label.
        hatchtime && ParsedTimeRenderer(hatchtime),
    },
    { primary: 'debug' },
  ],
  FailedRecord: [
    { primary: 'metadata_vendor' },
    { primary: 'metadata_product' },
    { primary: 'metadata_deviceEventId' },
    {
      primary: 'Reason',
      renderer: (_, record) => (
        <RecordValue
          hideDots={true}
          name={record.fields.reason}
          record={record}
        >
          {record.fields.reason}
        </RecordValue>
      ),
    },
  ],
  Email: [
    { primary: 'srcDevice_ip', secondary: 'srcPort' },
    { primary: 'dstDevice_ip', secondary: 'dstPort' },
    {
      renderer: (value, record) => {
        const { email_sender, user_email } = record;
        const result: string[] = [];
        if (email_sender) {
          result.push('Sender: {email_sender}');
        }

        // Recipient
        if (user_email) {
          result.push('Recipient: {user_email}');
        }

        return result.length
          ? getRenderer(result.join(', '))(value, record)
          : 'None';
      },
    },
  ],
  Endpoint: [
    { primary: 'metadata_vendor', secondary: 'metadata_product' },
    { renderer: infoRenderers.Endpoint, secondary: 'action' },
  ],
  EndpointModuleLoad: [
    { primary: 'metadata_vendor', secondary: 'metadata_product' },
    { renderer: infoRenderers.EndpointModuleLoad },
  ],
  EndpointProcess: [
    { primary: 'metadata_vendor', secondary: 'metadata_product' },
    { renderer: infoRenderers.EndpointProcess, secondary: 'file_path' },
  ],
  Network: [
    { primary: 'srcDevice_ip', secondary: 'srcPort' },
    { primary: 'dstDevice_ip', secondary: 'dstPort' },
    { primary: 'description', secondary: 'threat_name' },
  ],
  NetworkDHCP: [
    { primary: 'srcDevice_ip', secondary: 'srcPort' },
    { primary: 'dstDevice_ip', secondary: 'dstPort' },
    {
      renderer: getRenderer(
        'MAC Address {device_mac} assigned IP {device_ip}',
        'unknown'
      ),
    },
  ],
  NetworkDNS: [
    { primary: 'srcDevice_ip', secondary: 'srcPort' },
    { primary: 'dstDevice_ip', secondary: 'dstPort' },
    { primary: 'dns_query', secondary: 'dns_returnCode' },
  ],
  NetworkFlow: [
    { primary: 'srcDevice_ip', secondary: 'srcPort' },
    { primary: 'dstDevice_ip', secondary: 'dstPort' },
    { primary: 'ipProtocol', secondary: 'bro_flow_duration' },
  ],
  NetworkHTTP: [
    { primary: 'srcDevice_ip', secondary: 'srcPort' },
    { primary: 'dstDevice_ip', secondary: 'dstPort' },
    { renderer: infoRenderers.NetworkHTTP, secondary: 'http_hostname' },
  ],
  NetworkProxy: [
    { primary: 'metadata_vendor', secondary: 'metadata_product' },
    { renderer: infoRenderers.NetworkProxy, secondary: 'http_hostname' },
  ],
  Notification: [
    { primary: 'device_ip', secondary: 'device_hostname' },
    { primary: 'user_username' },
    { primary: 'description' },
  ],
  NotificationVulnerability: [
    { primary: 'device_ip', secondary: 'device_hostname' },
    { primary: 'user_username' },
    { primary: 'description', secondary: 'vuln_name' },
  ],
};
